// utils/getStationDisplayProps.ts
import SVG from "../components/svg/Svg"; // иконки

// ─── Константы состояний (убрать магические числа) ───
const STATION_OFFLINE = 0; // status_id: станция не на связи
const STATION_ONLINE = 1; // status_id: станция на связи

const STATE_FREE = 1;
const STATE_PREPARING = 2;
const STATE_CHARGING = 3;
const STATE_CHARGED = 4;
const STATE_UNAVAILABLE = 5; // админом выключена
const STATE_MODULE_OFFLINE = 6; // станция на связи, модуль нет (нет связи М)

/**
 * `СТ.2` (`oq-ММБ-298`, `ТЕСТ-Н111`): вид карточки — от ключа состояния, а не
 * от текста подписи. Один ключ на весь вид: класс `st-<kind>`, иконка и цвет
 * подписи. Смена слова подписи больше не ломает цвет молча.
 */
export type StationKind =
  | "free"
  | "waiting"
  | "paused"
  | "charging"
  | "finished"
  | "error"
  | "unavailable"
  | "not-ready"
  | "offline"
  | "module-offline";

// Интерфейс результата — всё, что нужно для отображения
type StationDisplayProps = {
  state: string; // текст состояния
  // `СТ.1`/`СТ.2`: класс карточки — по коду состояния, не по тексту подписи.
  kind: StationKind;
  icon: any;
  color: string; // цвет подписи и иконки
  inactive: boolean;
  // `С2` (`ТЕСТ-В33`): подсказка под статусом. Есть только у состояния 2 —
  // у остальных её нет, и пустая строка не выводится.
  hint: string | null;
};

/**
 * `С2` (`ТЕСТ-В33`, после `FW-165`): состояние 2 — «подключено, заряд не начат
 * или приостановлен». Подпись «подготовка» читалась как «идёт подготовка к
 * заряду», хотя заряд может и не начаться: разрешение даёт сервер.
 */
export const STATION_WAITING_HINT =
  "Автомобиль подключён. Заряд начнётся, когда будет разрешение";

/** `B.13`: остановка лимитом линии — коды `station_diag` 3 и 11. */
export const STATION_LINE_LIMIT_PAUSED = "приостановлено";
const LINE_LIMIT_STOP_REASONS = new Set([3, 11]);

// Тип станции
type Station = {
  status_id: number; // 0 - оффлайн, 1 - онлайн
  state_id: number; // см. константы STATE_*
  gps: string | null; // строка с ошибкой → если не пустая → ошибка (legacy-название поля)
  /** `B.13`: последняя сессия заряда; `stop_reason` 3/11 — лимит линии. */
  last_session?: { stop_reason?: number | null } | null;
};

const GREY = "var(--st-sign-grey)";

/**
 * Утверждённая иерархия приоритетов отображения:
 * 1. Нет связи (М)       — модуль не отвечает (state_id === 6)
 * 2. Нет связи           — станция полностью оффлайн (status_id === 0)
 * 3. Ошибка              — аппаратная ошибка (gps не пусто)
 * 4. Недоступно          — выключено административно (state_id === 5)
 * 5. Рабочие состояния   — свободно, ожидание, заряжается, завершено
 */
export const getStationDisplayProps = (
  station: Station,
): StationDisplayProps => {
  // 1. Нет связи с модулем (М)
  if (station.state_id === STATE_MODULE_OFFLINE) {
    return {
      state: "нет связи (М)",
      kind: "module-offline",
      icon: SVG.st_plug,
      color: GREY,
      inactive: true,
      hint: null,
    };
  }

  // 2. Станция полностью оффлайн
  if (station.status_id === STATION_OFFLINE) {
    return {
      state: "нет связи",
      kind: "offline",
      icon: SVG.st_ring,
      color: GREY,
      inactive: true,
      hint: null,
    };
  }

  // 3. Ошибка оборудования (поле gps — legacy-название, означает аппаратную ошибку)
  if (station.gps && station.gps.trim() !== "") {
    return {
      state: "ошибка",
      kind: "error",
      icon: SVG.st_alert,
      color: "var(--st-sign-error)",
      inactive: true,
      hint: null,
    };
  }

  // 4. Административно недоступно
  if (station.state_id === STATE_UNAVAILABLE) {
    return {
      state: "недоступно",
      kind: "unavailable",
      icon: SVG.st_minus,
      color: GREY,
      inactive: true,
      hint: "Станция не готова к заряду",
    };
  }

  // 5. Рабочие состояния
  switch (station.state_id) {
    case STATE_FREE:
      return {
        state: "свободно",
        kind: "free",
        icon: SVG.st_check,
        color: "var(--st-sign-free)",
        inactive: false,
        hint: null,
      };
    case STATE_PREPARING: {
      const stopReason = Number(station.last_session?.stop_reason);

      // `B.13`: станция ждёт, но в этой же сессии её остановил лимит линии.
      if (LINE_LIMIT_STOP_REASONS.has(stopReason)) {
        return {
          state: "приостановлено",
          kind: "paused",
          icon: SVG.st_pause,
          color: "var(--st-sign-wait)",
          inactive: false,
          hint: "Заряд приостановлен лимитом линии и продолжится сам",
        };
      }

      return {
        state: "ожидание",
        kind: "waiting",
        icon: SVG.st_clock,
        color: "var(--st-sign-wait)",
        inactive: false,
        hint: STATION_WAITING_HINT,
      };
    }
    case STATE_CHARGING:
      return {
        state: "заряжается",
        kind: "charging",
        icon: SVG.st_bolt,
        color: "var(--st-sign-charge)",
        inactive: false,
        hint: null,
      };
    case STATE_CHARGED:
      return {
        state: "завершено",
        kind: "finished",
        icon: SVG.st_check,
        color: "var(--st-sign-done)",
        inactive: false,
        hint: "Заряд завершён — отключите кабель",
      };

    case STATION_OFFLINE:
      // `СТ.1`: состояние 0 (`NeedInit`) — «не готова», серое и неактивное,
      // как «недоступно»; раньше молча показывалось «свободно».
      return {
        state: "не готова",
        kind: "not-ready",
        icon: SVG.st_gear,
        color: GREY,
        inactive: true,
        hint: "Станция не настроена — обратитесь к администратору",
      };

    default:
      // fallback — на случай неизвестного state_id
      return {
        state: "свободно",
        kind: "free",
        icon: SVG.st_check,
        color: "var(--st-sign-free)",
        inactive: false,
        hint: null,
      };
  }
};
